import dayjs from "dayjs";
import CryptoJS from "crypto-js";
import axios from "../tools/axios";

/**
 * OSS 直传凭证不在前端保存任何 AccessKey（旧 AK 已被阿里云禁用：InvalidAccessKeyId，
 * 且明文密钥打包进 JS 后对所有人可见，会被阿里云风控扫描禁用）。
 * 每次直传前调用后端接口 /SD/common/ossSign 换取签名凭证，接口约定见 fetchOssCredentials。
 */

// 站点是 https，所有地址都必须用 https，否则浏览器会按"混合内容(mixed content)"拦截
const endpoint = 'https://bonetoday.oss-cn-beijing.aliyuncs.com'
// 外网访问域名，同样用 https
const publicHost = 'https://oss.orthoday.com'
// 后端视频上传接口：POST /SD/common/movieUpload，表单字段 multipartFile，返回 OSS 地址
const movieUploadUrl = '/SD/common/movieUpload'
// 后端签名接口：上传专用 STS 临时凭证（详见 docs/getUploadSecurityToken.md）
// 不需要参数；返回 STS 凭证，policy 仅授权 bonetoday/app-movie/* 的 oss:PutObject
// 注意：接口已迁到 bonetoday-api 服务（context-path=/SD），旧的 /web/... 是 bonetoday-web 的同名接口
const ossSignUrl = '/SD/securityToken/getUploadSecurityToken'

// OSS 传输加速域名（已在控制台给 bonetoday 开启"传输加速"，会产生少量加速流量费）
// 跨地区、移动网络下会先就近接入阿里加速节点再走内网回源，上行通常比直连区域域名更快；
// 关闭时把 USE_ACCELERATE 改回 false 即可（CORS 规则按页面来源生效，对该域名同样适用）
const accelerateEndpoint = 'https://bonetoday.oss-accelerate.aliyuncs.com'
const USE_ACCELERATE = true
const uploadEndpoint = USE_ACCELERATE ? accelerateEndpoint : endpoint

/**
 * 直传候选端点：加速域名优先、区域域名兜底。
 * 两端点共用同一套签名（V1 签名只含 bucket/key，与 Host 无关），可以随时互换；
 * 上一次成功的端点会被提到最前，避免每次都先撞一遍失败的端点。
 */
let preferredEndpoint = ''
const directEndpoints = () => {
    const list = USE_ACCELERATE ? [accelerateEndpoint, endpoint] : [endpoint]
    const at = list.indexOf(preferredEndpoint)
    if (at > 0) {
        list.splice(at, 1)
        list.unshift(preferredEndpoint)
    }
    return list
}

const bucket = 'bonetoday'
// 超过 8M 走 OSS 分片并发直传：省掉服务器中转那一跳 + 多片并行，大文件提速最明显
// （8M 以上才有至少 2 片可并行，再小走单请求表单直传即可）
const MULTIPART_MIN_SIZE = 8 * 1024 * 1024
// 分片大小下限（OSS 要求单个分片 >= 100KB，5M 留足余量）
const MIN_PART_SIZE = 5 * 1024 * 1024
// 分片大小上限：再大单片失败重传代价太高
const MAX_PART_SIZE = 32 * 1024 * 1024
// 目标分片数：单片越大请求次数越少，但并行度和进度粒度会变粗，16 是折中值
const TARGET_PARTS = 16
// 单片失败重试次数
const PART_RETRY = 3

/**
 * 分片大小随文件大小自适应。
 * 固定 5M 对 200M 的视频意味着 40 次请求，移动网络 RTT 动辄几十上百毫秒，
 * 光请求往返就是一笔可观开销；放大分片能明显减少请求次数。
 */
const calcPartSize = (size) => {
    let partSize = Math.ceil(size / TARGET_PARTS)
    // 取整到整数 MB，签名和日志都更好读
    partSize = Math.ceil(partSize / 1048576) * 1048576
    if (partSize < MIN_PART_SIZE) partSize = MIN_PART_SIZE
    if (partSize > MAX_PART_SIZE) partSize = MAX_PART_SIZE
    return partSize
}

// 并发上限：到 OSS 的 HTTPS 走 HTTP/2（阿里云已支持），多路流复用同一条连接，
// 不受 HTTP/1.1"同域名最多 6 条连接"的限制，适当调高能更好占满带宽
const MAX_CONCURRENCY = 8

/**
 * 按网络状况挑并发数。
 * 之所以不用固定值：高延迟链路上单条 TCP 连接的吞吐上限 ≈ 窗口/RTT，
 * 多路并行才能把带宽占满；而弱网下开太多路又只会互相抢速，所以只有检测到
 * 高速网络（4G+/WiFi）时才往上加。
 * 注意：iOS 的 WKWebView（含微信内）不支持 navigator.connection，
 * 拿不到信息时给 6 而不是过去保守的 3/4：HTTPS 到 OSS 走 HTTP/2 多路复用，
 * 6 路是安全的，WiFi 下不会白白少跑好几路。
 */
const pickConcurrency = () => {
    const conn = (typeof navigator !== 'undefined' && navigator.connection) || null
    if (!conn) return 6
    if (conn.effectiveType && /^(slow-2g|2g|3g)$/.test(conn.effectiveType)) return 3
    const down = Number(conn.downlink) || 0
    if (down >= 10) return MAX_CONCURRENCY
    if (down >= 5) return 6
    return 5
}
// URL 签名有效期（秒）；注意实际生效时长还受 STS 凭证自身有效期限制
const SIGN_EXPIRES = 3600
// 分片内容的 Content-Type（最终文件的 Content-Type 以 InitiateMultipartUpload 为准）
const PART_CONTENT_TYPE = 'application/octet-stream'

/* 文件重命名（OSS 直传兜底时使用） */
const fileRename = (file, rootPath = "", fileType = [], size = 0) => {
    if (!(file instanceof Object)) return null;
    // 文件大小限制
    if (size && file.size >= size * 1024 * 1024) {
        console.warn(`上传的文件大小不能超过 ${size}M，请重新上传！`);
        return null;
    }

    let fileName = file.name.toLowerCase(),
        dotIndex = fileName.lastIndexOf("."),
        fileSuffix = fileName.substr(dotIndex);

    function uuid() {
        function S4() {
            return (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1);
        }
        return S4() + S4() + "-" + S4() + "-" + S4() + "-" + S4() + "-" + S4() + S4() + S4();
    }

    if (fileType === "pic") {
        // 文件名.pdf
        var timestamp = new Date().getTime();
        // 文件路径处理
        let filePath = dayjs().format("YYYY/MM/DD/") + timestamp + "/";

        return rootPath + filePath + decodeURI(fileName);
    } else {
        // 文件路径处理
        let filePath = dayjs().format("YYYY/MM/DD/") + uuid();
        return rootPath + filePath + fileSuffix;
    }
};

/* 接口返回的是 http 地址，统一转成 https */
const toHttps = (url) => String(url || '').replace(/^http:\/\//i, 'https://')

/* 提取错误信息，方便定位问题（413 / InvalidAccessKeyId ...） */
const formatError = (e) => {
    if (!e) return '未知错误'
    let msg = ''
    const res = e.response
    const data = res && res.data
    if (typeof data === 'string') {
        if (data.indexOf('<Code>') > -1) {
            // OSS 返回的 XML 错误
            const code = (data.match(/<Code>([\s\S]*?)<\/Code>/) || [])[1]
            const message = (data.match(/<Message>([\s\S]*?)<\/Message>/) || [])[1]
            msg = [code, message].filter(Boolean).join(' ')
        } else if (data.indexOf('413') > -1 || data.indexOf('Too Large') > -1) {
            msg = '文件超过服务器限制'
        } else if (res && res.status) {
            msg = 'HTTP' + res.status
        }
    }
    if (!msg && data && typeof data === 'object') msg = data.message || ''
    if (!msg) {
        msg = [e.code, e.name].filter(Boolean).join(' ')
        if (e.status) msg = (msg ? msg + ' ' : '') + 'HTTP' + e.status
        if (e.message) msg = (msg ? msg + ' ' : '') + e.message
    }
    if (!msg) msg = String(e)
    return String(msg).replace(/\s+/g, ' ').slice(0, 60)
}

/* ============ 直传通道临时熔断 ============ */
// 直传被 OSS 拒绝（403：RAM 权限 / 防盗链 / 签名 / AccessKey 失效）时，本次会话内不再反复尝试直传：
// 否则每次上传都要先撞一遍失败请求再退回服务器通道，只会让上传更慢、控制台更花。
// 只是"临时避让"，10 分钟后自动解除：权限修好后无需清缓存即可恢复直传。
const DIRECT_BLOCK_KEY = 'oss_direct_block_until'
const DIRECT_BLOCK_MS = 10 * 60 * 1000

const isDirectBlocked = () => {
    try {
        return Number(localStorage.getItem(DIRECT_BLOCK_KEY) || 0) > Date.now()
    } catch (e) {
        return false
    }
}

const blockDirect = (e) => {
    try { localStorage.setItem(DIRECT_BLOCK_KEY, String(Date.now() + DIRECT_BLOCK_MS)) } catch (err) {}
    console.warn(
        '[OSS] 直传失败（' + formatError(e) + '），' + (DIRECT_BLOCK_MS / 60000) +
        ' 分钟内改走后端 /SD/common/movieUpload 通道；若报 SignatureDoesNotMatch / AccessDenied，' +
        '请检查 STS 授权范围与签名方式，或后端 /SD/securityToken/getUploadSecurityToken 是否就绪'
    )
}

const clearDirectBlock = () => {
    try { localStorage.removeItem(DIRECT_BLOCK_KEY) } catch (e) {}
}

/* 只有鉴权/权限/防盗链类错误才熔断：网络抖动、超时等瞬时问题不熔断，避免误伤直传 */
const isAuthError = (e) => {
    const status = (e && e.response && e.response.status) || e.status
    const text = [e && e.message, e && e.response && e.response.data]
        .filter(v => typeof v === 'string')
        .join(' ')
    return status === 403 || status === 404 || status === 405 ||
        /AccessDenied|SignatureDoesNotMatch|InvalidAccessKeyId|AccessKeyDisabled|SecurityToken|Referer|Forbidden/i.test(text)
}

/* axios 的上传进度事件 -> 标准化进度对象
   对外 onProgress 既支持传数字（兼容旧用法，等同 percent）也支持传对象 { percent, loaded, total }。
   之所以改成对象：上层 UI 需要"已传 X MB / 总 Y MB"展示，仅有百分比做不出来。 */
const reportProgress = (onProgress, e) => {
    if (typeof onProgress !== 'function' || !e || !e.total) return
    const percent = Math.min(99, Math.floor((e.loaded / e.total) * 100))
    onProgress({ percent, loaded: e.loaded, total: e.total })
}

/* 最终文件的 Content-Type：必须按视频类型写，否则存上去变成 text/plain 影响播放 */
const objectContentType = (fileName) => {
    const ext = (String(fileName).toLowerCase().match(/\.([^.]+)$/) || [])[1] || ''
    const map = {
        mp4: 'video/mp4',
        mov: 'video/quicktime',
        m4v: 'video/x-m4v',
        webm: 'video/webm',
        avi: 'video/x-msvideo',
        mkv: 'video/x-matroska',
        mpg: 'video/mpeg',
        mpeg: 'video/mpeg',
        '3gp': 'video/3gpp',
        flv: 'video/x-flv'
    }
    return map[ext] || 'video/mp4'
}

/* 子资源按字典序拼接（OSS 签名要求） */
const canonicalSubResource = (params) => {
    const keys = Object.keys(params).filter(k => params[k] !== undefined && params[k] !== null)
    return keys.sort().map(k => (params[k] === '' ? k : k + '=' + params[k])).join('&')
}

/* ================= 后端签发的直传凭证 ================= */

/**
 * 凭证接口：GET / POST /SD/securityToken/getUploadSecurityToken（无需参数，无需 body）
 * 返回 { code: '1', result_data: { accessKeyId, accessKeySecret, securityToken,
 *   expireTime, bucket, dir, endpoint } }
 *
 * 注意（文档已声明，不要踩坑）：
 *   1. 返回的 `expireTime` 字段展示的是「当前+1天」，与实际 STS 有效期 **3600 秒** 不一致，
 *      不可信；前端必须按 3600 秒自己计时。
 *   2. 对象 Key 必须以 `app-movie/` 开头，否则 OSS 403（policy 仅授权该前缀）。
 *   3. 返回的 `endpoint` 是区域域名；前端仍可优先走传输加速域名（V1 签名不含 Host，可互换）。
 */
let credsCache = null

// 真实 STS 有效期：3600 秒（后端 AssumeRole durationSeconds；与返回的 expireTime 字段无关）
const STS_VALIDITY_MS = 3600 * 1000

const fetchOssCredentials = async (rootPath) => {
    // 提前 60 秒过期，避免上传到一半 STS 过期
    if (credsCache && credsCache.expireAt > Date.now() + 60 * 1000) return credsCache

    // 接口无需参数，POST 不带 body；这样 axios 不会主动加 Content-Type 触发后端误判
    const res = await axios.post(ossSignUrl, null, { timeout: 15 * 1000 })
    const data = res && res.data
    const rd = data && data.result_data
    if (!data || String(data.code) !== '1' || !rd || !rd.accessKeyId || !rd.accessKeySecret || !rd.securityToken) {
        throw new Error((data && data.message) || '获取 OSS 上传凭证失败')
    }

    // 这个接口只发 STS 凭证；如果后端临时改成 policy，前端会立即报错并回退后端通道
    const mode = 'sts'

    // Key 前缀校验：policy 只授权 rd.dir 下的对象，前端生成的 Key 必须以它开头
    const allowedDir = (rd.dir || 'app-movie/').replace(/^\/|\/$/g, '') + '/'
    if (rootPath && !rootPath.startsWith(allowedDir) && rootPath !== allowedDir) {
        console.warn(
            '[OSS] 期望上传到 "' + allowedDir + '"，但调用方传的根目录是 "' + rootPath +
            '"。OSS 会直接 403，请检查前端 BoneAdd/BoneEdit 传参'
        )
    }

    credsCache = {
        mode,
        accessKeyId: rd.accessKeyId,
        accessKeySecret: rd.accessKeySecret,
        securityToken: rd.securityToken,
        // STS 真实有效期 3600 秒；缓存按这个计时
        expireAt: Date.now() + STS_VALIDITY_MS,
        // 后端可能返回的扩展信息（暂未直接使用，留作后续 host / bucket 兜底）
        bucket: rd.bucket || bucket,
        dir: rd.dir || 'app-movie/',
        endpoint: rd.endpoint || endpoint
    }
    return credsCache
}

/**
 * 方式一：走后端视频上传接口（服务端持有 OSS 凭证，返回可直接访问的 OSS 地址）
 * 注意：服务器 nginx / spring 有大小限制，超限会返回 413
 */
const serverUpload = async (file, onProgress) => {
    const fd = new FormData()
    fd.append('multipartFile', file)

    const res = await axios.post(movieUploadUrl, fd, {
        timeout: 10 * 60 * 1000,
        onUploadProgress: (e) => reportProgress(onProgress, e)
    })
    const data = res && res.data
    const url = data && data.result_data
    if (data && String(data.code) === '1' && url) {
        return toHttps(url)
    }

    const err = new Error((data && data.message) || '视频上传接口返回异常')
    err.response = res
    throw err
}

/* ================= OSS 表单直传（PostObject） ================= */

/**
 * 构造 PostObject 表单所需的 policy 与签名。
 * - policy 模式：直接用后端返回的 policy / signature；
 * - sts 模式：前端用 STS 临时凭证生成（policy 条件须包含 x-oss-security-token）。
 */
const buildPostData = (fileName, creds) => {
    if (creds.mode === 'policy') {
        // 后端生成 key 时必须用它；没生成则用前端起的文件名
        return { key: creds.key || fileName, policy: creds.policy, signature: creds.signature }
    }

    // STS 模式：policy 到期时间跟凭证一致
    const expiration = new Date(creds.expireAt).toISOString().replace(/\.\d{3}Z$/, '.000Z')
    const conditions = [
        ['content-length-range', 0, 1024 * 1024 * 1024],
        ['eq', '$key', fileName]
    ]
    if (creds.securityToken) conditions.push({ 'x-oss-security-token': creds.securityToken })
    const policy = CryptoJS.enc.Base64.stringify(
        CryptoJS.enc.Utf8.parse(JSON.stringify({ expiration, conditions }))
    )
    const signature = CryptoJS.HmacSHA1(policy, creds.accessKeySecret).toString(CryptoJS.enc.Base64)
    return { key: fileName, policy, signature }
}

const ossUpload = async (fileName, file, onProgress, host, creds) => {
    const { key, policy, signature } = buildPostData(fileName, creds)
    const form = new FormData()
    // 注意：file 必须是最后一个字段
    form.append('key', key)
    form.append('policy', policy)
    form.append('OSSAccessKeyId', creds.accessKeyId)
    // STS 凭证必须随表单一起提交，且要参与 policy 签名
    if (creds.securityToken) form.append('x-oss-security-token', creds.securityToken)
    form.append('success_action_status', '200')
    form.append('signature', signature)
    form.append('file', file)

    const res = await axios.post(host, form, {
        timeout: 10 * 60 * 1000,
        onUploadProgress: (e) => reportProgress(onProgress, e)
    })
    if (res && res.status >= 200 && res.status < 300) return publicHost + '/' + key

    const err = new Error('OSS 返回状态 ' + (res && res.status))
    err.response = res
    throw err
}

/* ================= OSS 分片并发直传（大文件主力通道） ================= */

const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms))

/**
 * URL 签名：签名放进 query（OSSAccessKeyId / Expires / Signature），
 * 这样不需要自定义 Authorization / Date 头，跨域更省心。
 *
 * 【STS 关键点】security-token 不是子资源！
 * 必须作为 x-oss-security-token 请求头发送，并参与 CanonicalizedOSSHeaders 签名；
 * 若把它当子资源拼进 CanonicalizedResource，OSS 一律返回 SignatureDoesNotMatch（已实测验证）。
 *
 * 返回 { url, headers }，调用方发起请求时必须原样带上 headers。
 */
const signUrl = (method, key, params = {}, contentType = '', host = uploadEndpoint, creds = {}) => {
    // 子资源按字典序拼接（只含 uploads / uploadId / partNumber 这类真正的子资源）
    const subResource = canonicalSubResource(params)
    const expires = String(Math.floor(Date.now() / 1000) + SIGN_EXPIRES)
    const canonicalResource = '/' + bucket + '/' + key + (subResource ? '?' + subResource : '')

    const headers = {}
    if (contentType) headers['Content-Type'] = contentType

    // STS：token 既要随请求头发送，又要作为 CanonicalizedOSSHeaders 参与签名
    let ossHeaders = ''
    if (creds.securityToken) {
        ossHeaders = 'x-oss-security-token:' + creds.securityToken + '\n'
        headers['x-oss-security-token'] = creds.securityToken
    }

    const stringToSign = [
        method.toUpperCase(),
        '',                          // Content-MD5（无则空行）
        contentType,
        expires,
        ossHeaders + canonicalResource
    ].join('\n')
    const signature = CryptoJS.HmacSHA1(stringToSign, creds.accessKeySecret).toString(CryptoJS.enc.Base64)

    // 参与签名的用原值，拼 URL 的做转义
    const urlQuery = Object.keys(params)
        .filter(k => params[k] !== undefined && params[k] !== null)
        .map(k => (params[k] === '' ? k : k + '=' + encodeURIComponent(params[k])))
        .join('&')

    const query = (urlQuery ? urlQuery + '&' : '') +
        'OSSAccessKeyId=' + encodeURIComponent(creds.accessKeyId) +
        '&Expires=' + expires +
        '&Signature=' + encodeURIComponent(signature)

    return { url: host + '/' + encodeURI(key) + '?' + query, headers }
}

/* 1. 初始化分片上传，拿到 uploadId；同时在这里定下最终文件的 Content-Type */
const initMultipart = async (key, contentType, host, creds) => {
    const { url, headers } = signUrl('POST', key, { uploads: '' }, contentType, host, creds)
    const res = await axios.post(url, null, {
        headers,
        timeout: 30 * 1000
    })
    const xml = typeof res.data === 'string' ? res.data : ''
    const uploadId = (xml.match(/<UploadId>([\s\S]*?)<\/UploadId>/) || [])[1]
    if (!uploadId) throw new Error('OSS 分片上传初始化失败')
    return uploadId
}

/* 2. 上传单个分片 */
const uploadPart = async (key, uploadId, partNumber, blob, host, creds) => {
    const { url, headers } = signUrl('PUT', key, { partNumber: String(partNumber), uploadId }, PART_CONTENT_TYPE, host, creds)
    const res = await axios.put(url, blob, { headers, timeout: 5 * 60 * 1000 })
    const respHeaders = (res && res.headers) || {}
    // ETag 必须在 OSS 跨域规则里暴露（Expose-Header: ETag）才读得到
    const etag = respHeaders.etag || respHeaders.ETag || ''
    if (!etag) throw new Error('分片缺少 ETag（OSS 跨域未暴露 ETag）')
    return etag
}

/* 3. 合并分片 */
const completeMultipart = async (key, uploadId, parts, host, creds) => {
    const body = '<CompleteMultipartUpload>' +
        parts.map(p => '<Part><PartNumber>' + p.number + '</PartNumber><ETag>' + p.etag + '</ETag></Part>').join('') +
        '</CompleteMultipartUpload>'
    const { url, headers } = signUrl('POST', key, { uploadId }, 'application/xml', host, creds)
    await axios.post(url, body, {
        headers,
        timeout: 60 * 1000
    })
    return publicHost + '/' + key
}

/* 失败时清理已上传的分片，避免 OSS 里堆积垃圾 */
const abortMultipart = (key, uploadId, host, creds) => {
    if (!uploadId) return Promise.resolve()
    const { url, headers } = signUrl('DELETE', key, { uploadId }, '', host, creds)
    return axios.delete(url, { headers, timeout: 30 * 1000 }).catch(() => {})
}

/**
 * 分片并发直传：本地切片 -> 多片并行直传 OSS -> 合并
 * 相比整包上传：省掉服务器中转 + 多连接并发 + 单片失败只重传单片
 */
const multipartUpload = async (fileName, file, onProgress, host, creds) => {
    const size = file.size
    const partSize = calcPartSize(size)
    const contentType = objectContentType(fileName)
    const total = Math.max(1, Math.ceil(size / partSize))
    const concurrency = Math.min(pickConcurrency(), total)
    const uploadId = await initMultipart(fileName, contentType, host, creds)
    const startedAt = Date.now()

    const parts = new Array(total)
    let cursor = 0
    let done = 0
    let stopped = false

    const worker = async () => {
        while (!stopped) {
            const index = cursor++
            if (index >= total) return

            const start = index * partSize
            const blob = file.slice(start, Math.min(size, start + partSize))
            let lastError = null

            for (let attempt = 0; attempt < PART_RETRY; attempt++) {
                try {
                    const etag = await uploadPart(fileName, uploadId, index + 1, blob, host, creds)
                    parts[index] = { number: index + 1, etag }
                    done++
                    if (typeof onProgress === 'function') {
                        const percent = Math.min(99, Math.floor((done / total) * 100))
                        // 分片直传时 done * partSize 才是已传字节数（最后一个分片可能更小）
                        const loaded = Math.min(size, done * partSize)
                        onProgress({ percent, loaded, total: size })
                    }
                    lastError = null
                    break
                } catch (e) {
                    lastError = e
                    if (!stopped) await delay(500 * (attempt + 1))
                }
            }

            if (lastError) {
                stopped = true
                throw lastError
            }
        }
    }

    try {
        await Promise.all(new Array(concurrency).fill(0).map(() => worker()))
        const url = await completeMultipart(fileName, uploadId, parts, host, creds)

        // 打一行实测速度，方便判断瓶颈到底在网络还是别处
        const seconds = (Date.now() - startedAt) / 1000
        if (seconds > 0) {
            console.log(
                '[OSS] 分片上传完成 ' + (size / 1048576).toFixed(1) + 'MB / ' +
                total + '片 x ' + (partSize / 1048576).toFixed(0) + 'MB / ' +
                concurrency + '并发 / ' +
                seconds.toFixed(1) + 's / ' + (size / 1048576 / seconds).toFixed(2) + 'MB/s'
            )
        }
        return url
    } catch (e) {
        await abortMultipart(fileName, uploadId, host, creds)
        throw e
    }
}

/**
 * 上传视频
 * 返回 { url, error }，url 为空表示失败，error 为具体原因
 */
export default async (file, rootPath, fileType, size, onProgress) => {
    const fileName = fileRename(file, rootPath, fileType, size);
    if (!fileName) return { url: '', error: '文件不合法' };

    const errors = []
    /* 兼容外层两种调用方式：传数字（只关心百分比）或传对象（关心 loaded/total）。
       任何数值都会被规范化成统一的对象，避免上层做类型判断 */
    const report = (arg) => {
        if (typeof onProgress !== 'function') return
        if (typeof arg === 'number') {
            onProgress({ percent: arg, loaded: 0, total: file.size || 0 })
        } else if (arg && typeof arg === 'object') {
            onProgress(arg)
        }
    }

    // 直传前先向后端换凭证；接口未就绪 / 报错则熔断直传，直接走后端通道
    let creds = null
    if (!isDirectBlocked()) {
        try {
            creds = await fetchOssCredentials(rootPath)
        } catch (e) {
            console.warn('[OSS] 获取直传凭证失败：', formatError(e))
            blockDirect(e)
            creds = null
        }
    } else {
        console.warn('[OSS] 直传通道处于临时避让期，本次直接走后端上传通道')
    }

    // 在候选直传端点间依次尝试（加速域名 -> 区域域名），任一成功即返回
    const tryHosts = async (task) => {
        let lastError = null
        const hosts = directEndpoints()
        for (let i = 0; i < hosts.length; i++) {
            try {
                const url = await task(hosts[i])
                preferredEndpoint = hosts[i]
                clearDirectBlock()
                return url
            } catch (e) {
                lastError = e
                errors.push(formatError(e))
            }
        }
        // 所有端点都被拒才熔断通道；只要有一个能通就说明直传是好的
        if (isAuthError(lastError)) blockDirect(lastError)
        throw lastError || new Error('OSS 直传失败')
    }

    if (creds) {
        // 大文件 + 带密钥的凭证（STS / 普通 AK）：分片并发直传，速度最快
        if (creds.accessKeySecret && file && file.size && file.size >= MULTIPART_MIN_SIZE) {
            try {
                const url = await tryHosts(host => multipartUpload(fileName, file, report, host, creds))
                report(100)
                return { url, error: '' }
            } catch (e) {
                console.error('OSS 分片上传失败：', e)
                // 凭证失效（过期/权限）就清掉缓存，下次重新换
                if (isAuthError(e)) credsCache = null
            }
        }

        // 其余情况（中小文件，或只有 policy 凭证的大文件）：整文件表单直传，不经服务器中转
        try {
            const url = await tryHosts(host => ossUpload(fileName, file, report, host, creds))
            report(100)
            return { url, error: '' }
        } catch (e) {
            console.error('OSS 直传失败：', e)
            // 凭证失效（过期/权限）就清掉缓存，下次重新换
            if (isAuthError(e)) credsCache = null
        }
    }

    // 兜底：后端视频上传接口（不依赖签名接口，OSS 直传都不可用时仍能传上去）
    try {
        const url = await serverUpload(file, report)
        report(100)
        return { url, error: '' }
    } catch (e) {
        console.error('服务器视频上传失败：', e)
        errors.push('SERVER:' + formatError(e))
    }

    return { url: '', error: errors.join(' | ') }
};
